import React, { useState } from 'react';
import { 
  Plus, Edit, Trash2, Search, Upload, Download, 
  Building2, Star, Eye, EyeOff, Save, X, Check,
  Image, Link as LinkIcon, Percent, DollarSign,
  Users, Target, Zap, Gift, Tag, Phone, Mail,
  MessageSquare, MoreHorizontal, ChevronDown
} from 'lucide-react';
import BankOfferEditor from '../../../components/BankOfferEditor';

interface Bank {
  id: string;
  name: string;
  logo: string;
  color: string;
  isActive: boolean;
  isFeatured: boolean;
  stats: {
    customers: string;
    branches: string;
    atmCount: string;
    founded: string;
  };
  contact: {
    phone: string;
    email: string;
    website: string;
    address: string;
  };
  offers: string[];
  redirectUrl?: string;
  createdAt: string;
  updatedAt: string;
}

const BankManagement = () => {
  const [banks, setBanks] = useState<Bank[]>([
    {
      id: 'akbank',
      name: 'Akbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
      color: 'red',
      isActive: true,
      isFeatured: true,
      stats: {
        customers: '19M+',
        branches: '700+',
        atmCount: '5400+',
        founded: '1948'
      },
      contact: {
        phone: '0850 222 00 00',
        email: 'iletisim@akbank.com',
        website: 'https://www.akbank.com',
        address: 'Sabancı Center, 4. Levent, 34330 İstanbul'
      },
      offers: [
        '%0 Faizli 60.000 TL\'ye varan Nakit Fırsatı!',
        'Fatura talimatına, ayda 500TL, toplamda 1.500TL\'ye varan nakit iade'
      ],
      redirectUrl: 'https://mza5.tr.adj.st/akbankdirekt?function=609&widget=true&adj_t=16qmi3f5&adj_deep_link=akbankdirekt%3A%2F%2Fsplash%2Ffunction%2F609&adj_campaign=tuzeldob_50k&adj_fallback=https%3A%2F%2Fwww.akbank.com%2Fbasvuru%2Fmobileappservice%2F%23',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-18T10:00:00Z'
    },
    {
      id: 'garanti',
      name: 'Garanti BBVA',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
      color: 'green',
      isActive: true,
      isFeatured: true,
      stats: {
        customers: '17M+',
        branches: '850+',
        atmCount: '5000+',
        founded: '1946'
      },
      contact: {
        phone: '0850 222 0 333',
        email: 'iletisim@garantibbva.com.tr',
        website: 'https://www.garantibbva.com.tr',
        address: 'Nispetiye Mah. Aytar Cad. No:2, Beşiktaş, 34340 İstanbul'
      },
      offers: [
        '%0 Faizli 50.000 TL\'ye Varan İhtiyaç Kredisi!',
        '%0 Faizli 25.000 TL\'ye varan taksitli nakit avans!'
      ],
      redirectUrl: '',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-15T14:30:00Z'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [editingBank, setEditingBank] = useState<Bank | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<Partial<Bank>>({});
  const [isOfferEditorOpen, setIsOfferEditorOpen] = useState(false);
  const [selectedBankForOffers, setSelectedBankForOffers] = useState<Bank | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const filteredBanks = banks.filter(bank => {
    return bank.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleEdit = (bank: Bank) => {
    setEditingBank(bank);
    setFormData(bank);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingBank(null);
    setFormData({
      name: '',
      logo: '',
      color: 'blue',
      isActive: true,
      isFeatured: false,
      stats: {
        customers: '',
        branches: '',
        atmCount: '',
        founded: ''
      },
      contact: {
        phone: '',
        email: '',
        website: '',
        address: ''
      },
      offers: [],
      redirectUrl: ''
    });
    setIsModalOpen(true);
  };

  const handleSave = () => {
    if (editingBank) {
      setBanks(prev => prev.map(bank => 
        bank.id === editingBank.id 
          ? { ...bank, ...formData, updatedAt: new Date().toISOString() }
          : bank
      ));
    } else {
      const newBank: Bank = {
        ...formData as Bank,
        id: formData.name?.toLowerCase().replace(/\s+/g, '-') || Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      setBanks(prev => [...prev, newBank]);
    }
    setIsModalOpen(false);
    setEditingBank(null);
    setFormData({});
  };

  const handleDelete = (bankId: string) => {
    if (confirm('Bu bankayı silmek istediğinizden emin misiniz?')) {
      setBanks(prev => prev.filter(bank => bank.id !== bankId));
    }
  };

  const toggleBankStatus = (bankId: string) => {
    setBanks(prev => prev.map(bank => 
      bank.id === bankId 
        ? { ...bank, isActive: !bank.isActive, updatedAt: new Date().toISOString() }
        : bank
    ));
  };

  const toggleFeatured = (bankId: string) => {
    setBanks(prev => prev.map(bank => 
      bank.id === bankId 
        ? { ...bank, isFeatured: !bank.isFeatured, updatedAt: new Date().toISOString() }
        : bank
    ));
  };

  const handleEditOffers = (bank: Bank) => {
    setSelectedBankForOffers(bank);
    setIsOfferEditorOpen(true);
  };

  const handleSaveOffers = (bankId: string, offers: string[]) => {
    setBanks(prev => prev.map(bank => 
      bank.id === bankId 
        ? { ...bank, offers, updatedAt: new Date().toISOString() }
        : bank
    ));
  };

  const toggleDropdown = (id: string) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  // Function to highlight monetary values with marker effect
  const highlightMonetaryValues = (text: string) => {
    if (!text) return text;
    
    // Regular expression to match monetary values like "25.000 TL" or "1.500TL"
    const regex = /(\d+(?:\.\d+)?)\s*(?:TL|₺|TLL)/g;
    
    // Split the text by monetary values and keep the delimiters
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Create a new regex object to use with exec
    const regexObj = new RegExp(regex);
    
    // Find all matches and their positions
    while ((match = regexObj.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the match as a React element
      parts.push(
        <span key={`money-${match.index}`} className="bg-[#F7BA45]/30 px-1 py-0.5 rounded font-semibold">
          {match[0]}
        </span>
      );
      
      // Update lastIndex to continue after this match
      lastIndex = match.index + match[0].length;
    }
    
    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Banka Yönetimi</h1>
              <p className="text-slate-600">Bankaları ekleyin, düzenleyin ve yönetin</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={20} />
                <span>Dışa Aktar</span>
              </button>
              <button 
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Plus size={20} />
                <span>Yeni Banka</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <input
              type="text"
              placeholder="Banka ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            />
          </div>
        </div>

        {/* Banks Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Banka
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İstatistikler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Fırsatlar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Son Güncelleme
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredBanks.map((bank) => (
                  <tr key={bank.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <img 
                          src={bank.logo} 
                          alt={bank.name}
                          className="h-10 w-10 object-contain"
                        />
                        <div>
                          <div className="text-sm font-medium text-slate-900">{bank.name}</div>
                          <div className="text-xs text-slate-500">{bank.contact.website}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <div className="text-xs text-slate-500">Müşteri: <span className="text-slate-700 font-medium">{bank.stats.customers}</span></div>
                        <div className="text-xs text-slate-500">Şube: <span className="text-slate-700 font-medium">{bank.stats.branches}</span></div>
                        <div className="text-xs text-slate-500">ATM: <span className="text-slate-700 font-medium">{bank.stats.atmCount}</span></div>
                        <div className="text-xs text-slate-500">Kuruluş: <span className="text-slate-700 font-medium">{bank.stats.founded}</span></div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 max-w-xs">
                        {bank.offers.map((offer, index) => (
                          <div key={index} className="text-xs text-slate-600 truncate" title={offer}>
                            {highlightMonetaryValues(offer)}
                          </div>
                        ))}
                        {bank.offers.length === 0 && (
                          <div className="text-xs text-slate-500 italic">
                            Fırsat yok
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          bank.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {bank.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                          {bank.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        {bank.isFeatured && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45]/10 text-[#F7BA45] text-xs font-medium">
                            <Star size={12} />
                            Öne Çıkan
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        {new Date(bank.updatedAt).toLocaleDateString('tr-TR')}
                      </div>
                      <div className="text-xs text-slate-500">
                        {new Date(bank.updatedAt).toLocaleTimeString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <button
                          onClick={() => toggleDropdown(bank.id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <MoreHorizontal size={18} className="text-slate-500" />
                        </button>
                        
                        {activeDropdown === bank.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                            <button
                              onClick={() => {
                                handleEdit(bank);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Edit size={16} className="text-blue-500" />
                              <span>Düzenle</span>
                            </button>
                            <button
                              onClick={() => {
                                handleEditOffers(bank);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Gift size={16} className="text-[#F7BA45]" />
                              <span>Fırsatları Düzenle</span>
                            </button>
                            <button
                              onClick={() => {
                                toggleBankStatus(bank.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              {bank.isActive ? (
                                <>
                                  <EyeOff size={16} className="text-slate-500" />
                                  <span>Pasif Yap</span>
                                </>
                              ) : (
                                <>
                                  <Eye size={16} className="text-green-500" />
                                  <span>Aktif Yap</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                toggleFeatured(bank.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Star size={16} className={bank.isFeatured ? "text-[#F7BA45]" : "text-slate-500"} />
                              <span>{bank.isFeatured ? 'Öne Çıkarma' : 'Öne Çıkar'}</span>
                            </button>
                            <button
                              onClick={() => {
                                handleDelete(bank.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 size={16} />
                              <span>Sil</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredBanks.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <Building2 size={40} className="text-slate-300 mb-2" />
                        <p>Hiç banka bulunamadı</p>
                        <button 
                          onClick={handleAdd}
                          className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors text-sm"
                        >
                          <Plus size={16} />
                          <span>Yeni Banka Ekle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit/Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    {editingBank ? 'Banka Düzenle' : 'Yeni Banka Ekle'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column */}
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div>
                      <h3 className="text-lg font-medium text-slate-800 mb-4">Temel Bilgiler</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Banka Adı
                          </label>
                          <input
                            type="text"
                            value={formData.name || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Logo URL
                          </label>
                          <input
                            type="url"
                            value={formData.logo || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, logo: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Renk
                          </label>
                          <select
                            value={formData.color || 'blue'}
                            onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          >
                            <option value="blue">Mavi</option>
                            <option value="red">Kırmızı</option>
                            <option value="green">Yeşil</option>
                            <option value="purple">Mor</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Yönlendirme URL
                          </label>
                          <input
                            type="url"
                            value={formData.redirectUrl || ''}
                            onChange={(e) => setFormData(prev => ({ ...prev, redirectUrl: e.target.value }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.isActive || false}
                              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                            />
                            <span className="text-sm text-slate-700">Aktif</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.isFeatured || false}
                              onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                              className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                            />
                            <span className="text-sm text-slate-700">Öne Çıkan</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Stats */}
                    <div>
                      <h3 className="text-lg font-medium text-slate-800 mb-4">İstatistikler</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Müşteri Sayısı
                          </label>
                          <input
                            type="text"
                            value={formData.stats?.customers || ''}
                            onChange={(e) => setFormData(prev => ({ 
                              ...prev, 
                              stats: { ...prev.stats, customers: e.target.value } 
                            }))}
                            placeholder="Örn: 19M+"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Şube Sayısı
                          </label>
                          <input
                            type="text"
                            value={formData.stats?.branches || ''}
                            onChange={(e) => setFormData(prev => ({ 
                              ...prev, 
                              stats: { ...prev.stats, branches: e.target.value } 
                            }))}
                            placeholder="Örn: 700+"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            ATM Sayısı
                          </label>
                          <input
                            type="text"
                            value={formData.stats?.atmCount || ''}
                            onChange={(e) => setFormData(prev => ({ 
                              ...prev, 
                              stats: { ...prev.stats, atmCount: e.target.value } 
                            }))}
                            placeholder="Örn: 5400+"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Kuruluş Yılı
                          </label>
                          <input
                            type="text"
                            value={formData.stats?.founded || ''}
                            onChange={(e) => setFormData(prev => ({ 
                              ...prev, 
                              stats: { ...prev.stats, founded: e.target.value } 
                            }))}
                            placeholder="Örn: 1948"
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-6">
                    {/* Contact Info */}
                    <div>
                      <h3 className="text-lg font-medium text-slate-800 mb-4">İletişim Bilgileri</h3>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Telefon
                          </label>
                          <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                            <input
                              type="text"
                              value={formData.contact?.phone || ''}
                              onChange={(e) => setFormData(prev => ({ 
                                ...prev, 
                                contact: { ...prev.contact, phone: e.target.value } 
                              }))}
                              placeholder="0850 XXX XX XX"
                              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            E-posta
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                            <input
                              type="email"
                              value={formData.contact?.email || ''}
                              onChange={(e) => setFormData(prev => ({ 
                                ...prev, 
                                contact: { ...prev.contact, email: e.target.value } 
                              }))}
                              placeholder="iletisim@banka.com"
                              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Website
                          </label>
                          <div className="relative">
                            <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                            <input
                              type="url"
                              value={formData.contact?.website || ''}
                              onChange={(e) => setFormData(prev => ({ 
                                ...prev, 
                                contact: { ...prev.contact, website: e.target.value } 
                              }))}
                              placeholder="https://www.banka.com"
                              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Adres
                          </label>
                          <div className="relative">
                            <Building2 className="absolute left-3 top-3 text-slate-400" size={18} />
                            <textarea
                              value={formData.contact?.address || ''}
                              onChange={(e) => setFormData(prev => ({ 
                                ...prev, 
                                contact: { ...prev.contact, address: e.target.value } 
                              }))}
                              rows={3}
                              className="w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Offers */}
                    <div>
                      <h3 className="text-lg font-medium text-slate-800 mb-4">Fırsatlar</h3>
                      <div className="space-y-3">
                        {(formData.offers || []).map((offer, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div className="flex-1 p-3 bg-slate-50 rounded-lg">
                              {highlightMonetaryValues(offer)}
                            </div>
                            <button
                              onClick={() => {
                                const newOffers = [...(formData.offers || [])];
                                newOffers.splice(index, 1);
                                setFormData(prev => ({ ...prev, offers: newOffers }));
                              }}
                              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                        {(formData.offers || []).length < 3 && (
                          <button
                            onClick={() => {
                              const newOffers = [...(formData.offers || []), ''];
                              setFormData(prev => ({ ...prev, offers: newOffers }));
                            }}
                            className="w-full flex items-center justify-center gap-2 p-2 border border-dashed border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 hover:border-slate-400 transition-colors"
                          >
                            <Plus size={16} />
                            <span>Yeni Fırsat Ekle</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    İptal
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                  >
                    <Save size={20} />
                    <span>Kaydet</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bank Offer Editor Modal */}
      <BankOfferEditor
        isOpen={isOfferEditorOpen}
        onClose={() => {
          setIsOfferEditorOpen(false);
          setSelectedBankForOffers(null);
        }}
        bank={selectedBankForOffers}
        onSave={handleSaveOffers}
      />
    </div>
  );
};

export default BankManagement;